import React, {useEffect, useRef, useState} from 'react';
import {getScadaData, getScadaImageUrl} from "./scadaUtils";
import {ScadaElementsProps, ScadaProps} from "./scadaTypes";
import {ScadaTag} from "./ScadaTag";
import "./Scada.scss"
import {Dialog} from "primereact/dialog";
import DatasetLineChart from "../charts/dataset-line-chart/DatasetLineChart";
import {DatasetDataInfo} from "models/dataset-models";
import {Button} from "primereact/button";
import CalendarWrapper from "../../basic/calendar-wrapper/CalendarWrapper";
import {useResizeDetector} from "react-resize-detector";
import {DatasetDataInfoResponse} from "../../../api/model/dataset/DatasetDataInfoResponse";

export default function Scada(props: ScadaProps) {
    const {ref: windowRef, width} = useResizeDetector()

    const [dialog, setDialog] = useState<boolean>(false);
    const [selectedIdentifiers, setSelectedIdentifiers] = useState<Array<string>>([]);
    const [dateTime, setDateTime] = useState<{ date: Date, manualTime: boolean }>({
        date: new Date(),
        manualTime: false
    });

    const ref = useRef<HTMLDivElement>()

    useEffect(() => {
        ref.current?.addEventListener("click", cancelSelections);
        ref.current?.addEventListener("mousemove", showCords);
        ref.current?.addEventListener("contextmenu", handleContextMenu)

        return () => {
            ref.current?.removeEventListener("click", cancelSelections);
            ref.current?.removeEventListener("mousemove", showCords)
            ref.current?.removeEventListener("contextmenu", handleContextMenu)
        }
    }, [ref])

    const handleContextMenu = (e: any) => {
        let isEditMode = window.isEditMode;
        if (!isEditMode && window.scada.selectedLabels.length !== 0) {
            openScadaContextMenu(e, () => {
                openDialog()
            })
        }

        e.preventDefault();
    }

    const cancelSelections = (e: MouseEvent) => {
        if (e.ctrlKey) return;

        if (new Date().getTime() - window.scada.lastClick < 500) return;

        for (const el of window.scada.selectedLabels) {
            el.classList.remove("selectedLabel")
        }

        window.scada.selectedLabels = [];
    }

    const openDialog = () => {
        setSelectedIdentifiers([...new Set(window.scada.selectedLabels.map(l => l.dataset.identifier))])
        setDialog(true)
    }

    const elemProps = {
        ...props,
        imageWidth: width,
        date: dateTime.manualTime ? dateTime.date : undefined,
        returnDate: (e) => setDateTime({...dateTime, date: e}),
        openDialogCallBack: openDialog
    } as ScadaElementsProps

    return <>
        <div id={"tooltip-container"}></div>

        <div className={"flex flex-column gap-3"}>

            {props.showScadaSettings &&
                <div className={"flex gap-1"}>
                    <CalendarWrapper value={dateTime.date}
                                     onChange={(e) => setDateTime({date: e, manualTime: true})}
                    />

                    {dateTime.manualTime &&
                        <Button icon={"pi pi-sync"}
                                disabled={!dateTime.manualTime}
                                onClick={() => {
                                    setDateTime({
                                        date: new Date(),
                                        manualTime: false
                                    })
                                }}
                        />
                    }
                </div>
            }

            <div ref={r => {
                ref.current = r as HTMLDivElement
                windowRef.current = r
            }} className="scadaViewContainer relativePos">
                <img alt="SCADA schematics" className="scadaView" src={getScadaImageUrl(props)}/>

                <ScadaElements {...elemProps}/>
            </div>

        </div>


        <Dialog header="SCADA tags chart"
                visible={dialog}
                style={{width: 'min(100vw, 1000px)', height: "500px"}}
                modal={false}
                resizable={true}
                draggable={true}
                maximizable
                onHide={() => setDialog(false)}
        >
            <DatasetLineChart identifiers={selectedIdentifiers}
                              referenceTime={dateTime.manualTime ? dateTime.date.toISOString() : undefined}
                              showDateSelection={true} dynamicHeight={true} chartDialogOpen={dialog}/>
        </Dialog>
    </>
}

function ScadaElements(props: ScadaElementsProps) {
    const [dataMap, setDataMap] = useState<Map<String, DatasetDataInfo>>(new Map());

    useEffect(() => {
        setDataMap(new Map())
        loadData()

        const int = setInterval(loadData, (props.refreshRate ?? 60) * 1000);

        return () => {
            console.log("Cleaning up SCADA refresh interval")
            clearInterval(int);
        }
    }, [props.date])

    const loadData = () => {
        const chunkSize = 16
        const tmpMap = dataMap ?? new Map();
        let count = 0
        const identifiers = Object.keys(props.scadaLabelMap);

        while (count < identifiers.length) {
            const chunk = identifiers.slice(count, count += chunkSize)

            getScadaData(chunk, props.date?.toISOString()).then((data: Array<DatasetDataInfoResponse>) => {
                const currentDate = new Date()
                data.forEach(d => {
                    tmpMap.set(
                        d.identifier,
                        {...d, loadedTime: props.date || currentDate} as DatasetDataInfo)
                });

                if (!props.date) {
                    props.returnDate(currentDate)
                }

                setDataMap(new Map(tmpMap));
            })
        }
    }

    return <>
        {Object.keys(props.scadaLabelMap).map((v) => {
            const l = props.scadaLabelMap[v];
            const dataset = dataMap.get(v)

            return <ScadaTag key={v}
                             model={props}
                             x={l.xCoord} y={l.yCoord}
                             dataset={dataset} identifier={v}
                             bgColor={l.bgColor} fontSize={l.fontSize}
                             refTime={props.date}
            />
        })}
    </>
}